import { useHead } from '@unhead/vue';
import { useTranslation } from 'i18next-vue';

interface PageData {
    /** i18n key */
    title: string;
    /** i18n key */
    description: string;
    /**
     * i18n key
     * If translation is empty, a default image will be used
     */
    image: string;
    /** @default true */
    index?: boolean;
}

const defaultImg = `${window.location.origin}/images/og_imagesY2K.png`;

/**
 * Defines page meta data using the provided i18n keys.
 */
export function usePageMeta(data: PageData, i18nNamespace = 'tags') {
    // Somehow, we need to specify the namepsace that should be used here.
    // Otherwise the translations are not available when the meta tags are constructed on first page load.
    const { t } = useTranslation(i18nNamespace);

    useHead({
        title: () => t(data.title),
        titleTemplate: null,
        meta: [
            {
                name: 'robots',
                content: !data?.index
                    ? 'noindex,nofollow,noarchive,noodp,nosnippet'
                    : 'index,follow,noarchive,noodp,nosnippet',
            },
            { property: 'fb:app_id', content: import.meta.env.MIX_FACEBOOK_ID },
            { property: 'og:title', content: () => t(data.title) },
            { property: 'og:description', content: () => t(data.description) },
            { property: 'og:image', content: t(data.image) || defaultImg },
            {
                property: 'og:image:secure_url',
                content: () => t(data.image) || defaultImg,
            },
            { property: 'og:url', content: window.location.href },
            { property: 'og:type', content: 'website' },
            { name: 'description', content: () => t(data.description) },
        ],
    });
}
